import { useRef, useState } from 'react'
import {
  ArrowRight,
  Expand,
  X,
  Bell,
  BookOpen,
  Bot,
  CheckCheck,
  ChevronDown,
  FileText,
  GraduationCap,
  HeartHandshake,
  Inbox,
  LayoutTemplate,
  ListChecks,
  Puzzle,
  Rocket,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  SquareKanban,
  Terminal,
  Users,
  Workflow,
} from 'lucide-react'
import { m } from '#/paraglide/messages'
import { Container, MkLink, Overline, SectionHead } from './mk'

export function ProductExplorer() {
  const [selected, setSelected] = useState(0)
  const preview = useRef<HTMLDialogElement>(null)
  const views = [
    {
      label: m.mk_preview_plan(),
      title: m.mk_preview_plan_title(),
      desc: m.mk_preview_plan_desc(),
      src: '/landing/prd-review.png',
      icon: FileText,
    },
    {
      label: m.mk_preview_roadmap(),
      title: m.mk_preview_roadmap_title(),
      desc: m.mk_preview_roadmap_desc(),
      src: '/landing/hero-roadmap.png',
      icon: Workflow,
    },
    {
      label: m.mk_preview_tasks(),
      title: m.mk_preview_tasks_title(),
      desc: m.mk_preview_tasks_desc(),
      src: '/landing/kanban.png',
      icon: SquareKanban,
    },
    {
      label: m.mk_preview_wiki(),
      title: m.mk_preview_wiki_title(),
      desc: m.mk_preview_wiki_desc(),
      src: '/landing/wiki.png',
      icon: BookOpen,
    },
  ]
  return (
    <section id="product" className="mk-section">
      <Container>
        <SectionHead
          overline={m.mk_platform_overline()}
          title={m.mk_platform_title()}
        />
        <div
          className="mk-product-tabs"
          role="tablist"
          aria-label={m.mk_preview_label()}
        >
          {views.map((view, index) => (
            <button
              key={view.label}
              id={`product-tab-${index}`}
              type="button"
              role="tab"
              aria-selected={selected === index}
              aria-controls={`product-panel-${index}`}
              tabIndex={selected === index ? 0 : -1}
              onClick={() => setSelected(index)}
              onKeyDown={(event) => {
                const next =
                  event.key === 'ArrowRight'
                    ? (index + 1) % views.length
                    : event.key === 'ArrowLeft'
                      ? (index + views.length - 1) % views.length
                      : event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                          ? views.length - 1
                          : null
                if (next === null) return
                event.preventDefault()
                setSelected(next)
                document.getElementById(`product-tab-${next}`)?.focus()
              }}
            >
              <view.icon className="size-4" aria-hidden />
              {view.label}
            </button>
          ))}
        </div>
        {views.map((view, index) => (
          <div
            key={view.label}
            id={`product-panel-${index}`}
            role="tabpanel"
            aria-labelledby={`product-tab-${index}`}
            tabIndex={0}
            hidden={selected !== index}
            className="mk-product-panel"
          >
            <div className="mk-product-copy">
              <h3>{view.title}</h3>
              <p className="mk-muted">{view.desc}</p>
              <MkLink
                to="/sign-in"
                search={{ redirect: '/home' }}
                variant="secondary"
                arrow
              >
                {m.mk_hero_cta_primary()}
              </MkLink>
            </div>
            <figure>
              <img
                src={view.src}
                alt={view.title}
                width={1440}
                height={900}
                loading="lazy"
                decoding="async"
              />
              <figcaption>{m.mk_preview_caption()}</figcaption>
              <button
                type="button"
                className="mk-preview-enlarge"
                aria-haspopup="dialog"
                onClick={() => preview.current?.showModal()}
              >
                <Expand className="size-4" aria-hidden />
                {m.mk_preview_enlarge()}
              </button>
            </figure>
          </div>
        ))}
        <dialog
          ref={preview}
          className="mk-preview-dialog"
          aria-labelledby="mk-preview-title"
        >
          <div className="mk-preview-dialog-head">
            <h3 id="mk-preview-title">{views[selected].title}</h3>
            <button
              type="button"
              onClick={() => preview.current?.close()}
              aria-label={m.mk_preview_close()}
            >
              <X className="size-5" aria-hidden />
            </button>
          </div>
          <p>{m.mk_preview_pan()}</p>
          <div
            className="mk-preview-scroll"
            role="region"
            tabIndex={0}
            aria-label={views[selected].title}
          >
            <img
              src={views[selected].src}
              alt={views[selected].title}
              width={1440}
              height={900}
              loading="lazy"
            />
          </div>
        </dialog>
      </Container>
    </section>
  )
}

export function BuildPaths() {
  const paths = [
    {
      icon: Bot,
      title: m.mk_build_auto_title(),
      desc: m.mk_build_auto_desc(),
      note: m.mk_build_auto_note(),
      cta: m.mk_build_auto_cta(),
      to: '/otomatis' as const,
    },
    {
      icon: HeartHandshake,
      title: m.mk_build_guided_title(),
      desc: m.mk_build_guided_desc(),
      note: m.mk_build_guided_note(),
      cta: m.mk_build_guided_cta(),
      to: '/consulting' as const,
    },
    {
      icon: Terminal,
      title: m.mk_build_manual_title(),
      desc: m.mk_build_manual_desc(),
      note: m.mk_build_manual_note(),
      cta: m.mk_build_manual_cta(),
      to: '/serve' as const,
    },
  ]
  return (
    <section id="build" className="mk-section bg-[var(--mk-bg-muted)]">
      <Container>
        <SectionHead
          overline={m.mk_build_overline()}
          title={m.mk_build_title()}
          lead={m.mk_build_sub()}
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {paths.map((path, index) => (
            <article key={path.title} className="mk-path-card">
              <div className="flex items-center justify-between">
                <path.icon className="size-6" aria-hidden />
                <span className="font-mono text-sm text-[var(--mk-fg-muted)]">
                  0{index + 1}
                </span>
              </div>
              <h3>{path.title}</h3>
              <p>{path.desc}</p>
              <small>{path.note}</small>
              <MkLink to={path.to} variant="secondary" arrow>
                {path.cta}
              </MkLink>
            </article>
          ))}
        </div>
      </Container>
    </section>
  )
}

export function FeatureDirectory() {
  const features = [
    {
      icon: Users,
      title: m.mk_tb_feature_title(),
      desc: m.mk_tb_feature_desc(),
      href: '/tutorial#team-build',
    },
    {
      icon: Inbox,
      title: m.mk_feature_1_title(),
      desc: m.mk_feature_1_desc(),
      href: '/docs',
    },
    {
      icon: ShieldCheck,
      title: m.mk_feature_2_title(),
      desc: m.mk_feature_2_desc(),
      href: '/docs',
    },
    {
      icon: Bell,
      title: m.mk_feature_3_title(),
      desc: m.mk_feature_3_desc(),
      href: '/docs',
    },
    {
      icon: Sparkles,
      title: m.mk_feature_4_title(),
      desc: m.mk_feature_4_desc(),
      href: '/integrations',
    },
    {
      icon: LayoutTemplate,
      title: m.mk_feature_5_title(),
      desc: m.mk_feature_5_desc(),
      href: '/templates',
    },
    {
      icon: Puzzle,
      title: m.mk_feature_6_title(),
      desc: m.mk_feature_6_desc(),
      href: '/integrations',
    },
    {
      icon: GraduationCap,
      title: m.mk_feature_7_title(),
      desc: m.mk_feature_7_desc(),
      href: '/campus',
    },
    {
      icon: HeartHandshake,
      title: m.mk_feature_8_title(),
      desc: m.mk_feature_8_desc(),
      href: '/tools',
    },
  ]
  return (
    <section id="capabilities" className="mk-section">
      <Container>
        <SectionHead
          align="left"
          overline={m.mk_features_overline()}
          title={m.mk_features_title()}
          lead={m.mk_features_sub()}
        />
        <div className="mk-feature-grid">
          {features.map((feature) => (
            <a
              key={feature.title}
              href={feature.href}
              className="mk-feature-link"
            >
              <feature.icon className="size-5" aria-hidden />
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
              <span>
                {m.mk_feature_link()}
                <ArrowRight className="size-4" aria-hidden />
              </span>
            </a>
          ))}
        </div>
      </Container>
    </section>
  )
}

export function PublishSection() {
  const steps = [
    {
      icon: ListChecks,
      title: m.mk_publish_1_title(),
      desc: m.mk_publish_1_desc(),
    },
    {
      icon: Rocket,
      title: m.mk_publish_2_title(),
      desc: m.mk_publish_2_desc(),
    },
    {
      icon: RotateCcw,
      title: m.mk_publish_3_title(),
      desc: m.mk_publish_3_desc(),
    },
  ]
  return (
    <section id="publish" className="mk-section bg-[var(--mk-bg-muted)]">
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col items-start gap-5">
            <Overline>{m.mk_publish_overline()}</Overline>
            <h2>{m.mk_publish_title()}</h2>
            <p className="mk-lead">{m.mk_publish_sub()}</p>
            <MkLink to="/otomatis" variant="secondary" arrow>
              {m.mk_publish_cta()}
            </MkLink>
            <p className="mk-muted max-w-[55ch] text-sm leading-relaxed">
              {m.mk_publish_note()}
            </p>
          </div>
          <figure
            className="mk-publish-flow"
            aria-label={m.mk_publish_diagram()}
          >
            <ol>
              {steps.map((step, index) => (
                <li key={step.title}>
                  <div className="mk-publish-step">
                    <step.icon className="size-5" aria-hidden />
                    <span>0{index + 1}</span>
                  </div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  <CheckCheck
                    className="size-4 shrink-0 text-[var(--mk-fg-muted)]"
                    aria-hidden
                  />
                </li>
              ))}
            </ol>
            <figcaption>{m.mk_mock_label()}</figcaption>
          </figure>
        </div>
      </Container>
    </section>
  )
}

export function FaqSection() {
  const questions = [
    [m.mk_tb_faq_people_q(), m.mk_tb_faq_people_a()],
    [m.mk_tb_faq_start_q(), m.mk_tb_faq_start_a()],
    [m.mk_tb_faq_release_q(), m.mk_tb_faq_release_a()],
    [m.mk_tb_faq_issues_q(), m.mk_tb_faq_issues_a()],
    [m.mk_faq_1_q(), m.mk_faq_1_a()],
    [m.mk_faq_2_q(), m.mk_faq_2_a()],
    [m.mk_faq_3_q(), m.mk_faq_3_a()],
    [m.mk_faq_4_q(), m.mk_faq_4_a()],
    [m.mk_faq_5_q(), m.mk_faq_5_a()],
    [m.mk_faq_6_q(), m.mk_faq_6_a()],
    [m.mk_faq_7_q(), m.mk_faq_7_a()],
    [m.mk_faq_8_q(), m.mk_faq_8_a()],
    [m.mk_faq_native_q(), m.mk_faq_native_a()],
    [m.mk_faq_desktop_q(), m.mk_faq_desktop_a()],
    [m.mk_faq_team_q(), m.mk_faq_team_a()],
    [m.mk_faq_budget_q(), m.mk_faq_budget_a()],
  ]
  return (
    <section id="faq" className="mk-section">
      <Container>
        <SectionHead
          overline={m.mk_faq_label()}
          title={m.mk_faq_title()}
          lead={m.mk_faq_sub()}
        />
        <div className="mk-faq-list">
          {questions.map(([question, answer]) => (
            <details key={question}>
              <summary>
                {question}
                <ChevronDown className="size-5 shrink-0" aria-hidden />
              </summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </Container>
    </section>
  )
}
